import React, {
  FunctionComponent,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Select, { OptionsOrGroups, SingleValue, GroupBase } from 'react-select'
import {
  currentUserSelector,
  userOrganizationsSelector,
} from '../store/selectors/userSelector'
import { useSelector } from 'react-redux'
import { find, flatten } from 'lodash'
import { Dealership } from '../lib/types/Dealership'
import { ReactComponent as ChevronRight } from './../images/icons/chevron_right.svg'
import { ReactComponent as SearchIcon } from './../images/icons/search_icon.svg'
import { selectedOrganization } from '../store/selectors/organizationSelector'
import { useAtom } from 'jotai'
import { organizationLoadingAtom } from '../store/organizationAtom'
import { useNavigate } from 'react-router-dom'
import { useDealership, useDealershipStore } from '../store/zustand/dealershipStore'
import { GA4 } from '../lib/utils/ga4'
import { Organization } from '../lib/types/Organization'
import { match, P } from 'ts-pattern'
import mtm from '../lib/utils/mtm'
import { useTranslation } from 'react-i18next'
import {
  useInteractions,
  arrow,
  autoUpdate,
  shift,
  useFloating,
} from '@floating-ui/react'
import classNames from 'classnames'
import { User } from '../lib/types/User'
import { queryClient } from '../api/queryClient'

const styles = {
  control: (provided) => ({
    ...provided,
    minWidth: 320,
    maxWidth: 320,
    backgroundColor: '#423F62',
    border: 'none',
    margin: 8,
    maxHeight: 32,
    minHeight: 32,
    height: 32,
    color: '#FFF',
  }),
  valueContainer: (provided) => ({
    ...provided,
    maxHeight: 32,
    minHeight: 32,
    height: 32,
    color: '#FFF',
  }),
  input: (base) => ({
    ...base,
    color: '#FFF',
    fontFamily: 'Spartan',
    fontSize: 12,
    fontWeight: 600,
  }),
  menuList: (base) => ({
    ...base,
    zIndex: 100,
    borderRadius: 8,
    '::-webkit-scrollbar': {
      width: '0px',
    },
  }),
  dropdownIndicator: (base) => ({
    ...base,
    transform: 'rotate(-90deg)',
    color: '#fff',
    '&:hover': {
      color: '#fff',
    },
  }),
  option: (
    base,
    { isFocused, isSelected }: { isFocused?: boolean; isSelected?: boolean },
  ) => ({
    ...base,
    fontWeight: isSelected ? '700' : '600',
    fontFamily: 'Spartan',
    opacity: isSelected ? 1 : 0.8,
    fontSize: 14,
    marginLeft: 12,
    backgroundColor: isFocused ? '#121124' : '#121124',
    '&:hover': {
      opacity: 1,
      color: '#FFF',
    },
    '&:active': {
      backgroundColor: '#423F62',
    },
  }),
  menu: () => ({ boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)', marginTop: 0 }),
  groupHeading: (base) => ({
    ...base,
    color: '#837CD1',
    fontWeight: 'bold',
    backgroundColor: '#121124',
    paddingBottom: 8,
  }),
}

const Menu = (props: JSX.IntrinsicElements['div']): JSX.Element => {
  return (
    <div
      className="bg-white rounded absolute z-[2] mt-2 shadow-[0_0_0_1px_hsla(218, 50%, 10%, 0.1),0_4px_11px_hsla(218, 50%, 10%, 0.1)]"
      {...props}
    />
  )
}
const Blanket = (props: JSX.IntrinsicElements['div']): JSX.Element => (
  <div className="fixed bottom-0 left-0 top-0 right-0 z-[1]" {...props} />
)

const DropdownIndicator = (): JSX.Element => (
  <div className={'mr-2'}>
    <SearchIcon />
  </div>
)

interface DropdownProps {
  readonly isOpen: boolean
  readonly target: ReactNode
  readonly onClose: () => void
  readonly children: JSX.Element | false
}
const Dropdown: FunctionComponent<DropdownProps> = ({
  children,
  isOpen,
  target,
  onClose,
}) => (
  <div>
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
)

const DealershipSelect = (): JSX.Element => {
  const setDealership = useDealershipStore((state) => state.setDealership)
  const organizations = useSelector(userOrganizationsSelector)
  const dealerships = useMemo(() => {
    return flatten(
      organizations?.map((organization) => {
        return organization?.dealerships
      }),
    )
  }, [organizations])
  const currentDealership = useDealership()
  const currentOrganizationId = useSelector(selectedOrganization)
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [, setIsLoadingOrganization] = useAtom(organizationLoadingAtom)
  const user: User | null = useSelector(currentUserSelector)

  const { t } = useTranslation('routes')

  const selectedOption = useMemo(() => {
    return {
      value: `${currentDealership?.organization_id}-${currentDealership?.id}`,
      label: currentDealership?.dealership,
      agency_name: currentDealership?.organization_name,
    }
  }, [currentDealership])

  const options = useMemo(
    () =>
      match(organizations)
        .with(P.not(P.array(P.number)), (value) => {
          return value || []
        })
        .otherwise(() => [])
        ?.map((organization: Organization & { dealerships: Array<Dealership> }) => {
          const organization_name = organization.name
          const organization_id = organization.id
          return {
            label: organization_name,
            value: organization_id,
            options: organization?.dealerships
              ?.filter((dealership) => dealership?.dealership && dealership?.id)
              ?.map((dealership: Dealership) => ({
                value: `${organization_id}-${dealership.id}`,
                label: dealership.dealership ?? '',
              }))
              ?.filter((dealership) => dealership)
              ?.sort((a, b) => a.label.localeCompare(b.label)),
          }
        })
        .sort((a: { label: string }, b: { label: string }) =>
          a.label.localeCompare(b.label),
        ),
    [organizations],
  )

  const onSelectDealership = useCallback(
    async (
      value: SingleValue<{
        value: string
        label: string | undefined
      }>,
    ) => {
      const test = value?.value.split('-') ?? [0, 0]
      const organization_id = test[0]
      const dealership_id = test[1]
      const dealership = find(dealerships, {
        id: Number(dealership_id),
        organization_id: Number(organization_id),
      })
      GA4.sendEvent({
        event_name: 'select_dealership',
        event_params: {
          dealership_id: dealership?.id,
          dealership_name: dealership?.dealership,
        },
      })
      mtm.sendEvent('Select', 'Dealership', undefined, dealership?.id)
      if (dealership) {
        if (dealership?.organization_id !== currentOrganizationId) {
          setIsLoadingOrganization(true)
          queryClient.resetQueries({
            queryKey: ['dealership', currentDealership?.id],
            exact: false,
          })
        }
        navigate(
          t(`dealership.profile`, {
            dealership,
          }),
        )
        setDealership(dealership)
        setIsOpen(false)
        await queryClient.refetchQueries(['currentUser'])
        setTimeout(() => {
          setIsLoadingOrganization(false)
        }, 1000)
      }
    },
    [
      dealerships,
      currentOrganizationId,
      navigate,
      t,
      setDealership,
      setIsLoadingOrganization,
      currentDealership?.id,
    ],
  )

  const arrowRef = useRef(null)
  const { refs, strategy, x, y } = useFloating({
    placement: 'right-start',
    middleware: [shift(), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  })

  const { getFloatingProps } = useInteractions()
  const dropdownRef: MutableRefObject<HTMLDivElement | null> = useRef(null)

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target))
      setIsOpen(false)
  }

  useEffect(() => {
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    else document.removeEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <Dropdown
      isOpen={isOpen}
      onClose={() => setIsOpen((prev) => !prev)}
      target={
        <div
          onClick={(e) => {
            setIsOpen(true)
          }}
          ref={refs.setReference}
          className={
            'flex flex-row items-center bg-primary-independence p-2 font-Spartan cursor-pointer rounded-lg'
          }>
          <div
            className={
              'mx-2 truncate font-bold select-none text-white flex flex-col gap-0.5'
            }>
            <span className={'text-xs truncate'}>{selectedOption.label}</span>
            <span className={'text-primary-glaucous text-[10px] truncate uppercase'}>
              {selectedOption?.agency_name}
            </span>
          </div>
          <div className={'ml-auto'}>
            <ChevronRight fill={'white'} />
          </div>
        </div>
      }>
      {isOpen && (
        <div
          ref={(ref) => {
            refs.setFloating(ref)
            dropdownRef.current = ref
          }}
          className="absolute bg-blue-primary p-5 rounded-lg"
          {...getFloatingProps({
            style: {
              position: strategy,
              top: y ? y - 24 : 0,
              left: x ? x + 32 : 0,
            },
          })}>
          <Select
            components={{
              DropdownIndicator,
              IndicatorSeparator: () => null,
              // @ts-ignore
              GroupHeading: ({
                data,
                children,
              }: {
                data: { value: string; label: string | undefined }
                children: ReactNode
              }) => (
                <div
                  className={classNames(
                    'hover:text-white font-bold p-2 bg-primary-xiketic text-secondary-ultramarine-purple text-xs',
                    { 'cursor-pointer': (user?.role_id ?? 3) < 3 },
                  )}
                  onContextMenu={() => {
                    if ((user?.role_id ?? 3) < 3) {
                      window.open(
                        t('agency.profile.overview', {
                          agency_id: data.value,
                        }),
                        '_blank',
                      )
                    }
                  }}
                  onClick={() => {
                    if ((user?.role_id ?? 3) < 3) {
                      navigate(
                        t(`agency.profile.overview`, {
                          agency_id: data.value,
                        }),
                      )
                      setIsOpen(false)
                    }
                  }}>
                  {children}
                </div>
              ),
              Option: ({
                data,
                children,
              }: {
                data: { value: string; label: string | undefined }
                children: ReactNode
              }) => (
                <div
                  className="block hover:text-white !text-sm w-full px-2 py-2 opacity-80 font-semibold cursor-pointer bg-primary-xiketic text-xs ml-3"
                  onClick={() =>
                    onSelectDealership({
                      value: data.value,
                      label: data.label,
                    })
                  }
                  onContextMenu={() => {
                    const test = data.value?.split('-') ?? [0, 0]
                    const organization_id = test[0]
                    const dealership_id = test[1]
                    const dealership = find(dealerships, {
                      id: Number(dealership_id),
                      organization_id: Number(organization_id),
                    })
                    window.open(
                      t('dealership.profile', {
                        dealership: dealership,
                      }),
                      '_blank',
                    )
                  }}>
                  {children}
                </div>
              ),
            }}
            autoFocus
            backspaceRemovesValue={false}
            controlShouldRenderValue={false}
            hideSelectedOptions={false}
            isClearable={false}
            isSearchable={true}
            menuIsOpen
            styles={styles}
            onChange={onSelectDealership}
            options={
              options as OptionsOrGroups<
                {
                  value: string
                  label: string | undefined
                },
                GroupBase<{
                  value: string
                  label: string | undefined
                }>
              >
            }
            value={selectedOption}
            placeholder="Search..."
            tabSelectsValue={false}
            filterOption={({ label, value }, string) => {
              // default search
              if (label.toLocaleLowerCase().includes(string.toLocaleLowerCase()))
                return true
              // check if a group has the filter string as label
              const groupOptions = options.filter((group) =>
                group.label.toLocaleLowerCase().includes(string.toLocaleLowerCase()),
              )
              if (groupOptions) {
                for (const groupOption of groupOptions) {
                  const option = groupOption.options.find((opt) => opt.value === value)
                  if (option) return true
                }
              }
              return false
            }}
          />
          <div
            className="w-4 h-4 bg-blue-primary [left:-0.5rem] top-[40.5px] absolute transition transition-transform duration-200 ease-in-out rotate-45"
            ref={arrowRef}
          />
        </div>
      )}
    </Dropdown>
  )
}

export default DealershipSelect
